/**
 * Classificação de coberturas x serviços/assistências para as telas de análise
 * do PME (aba "Coberturas e Serviços").
 *
 * O BFF não expõe um flag confiável para separar as duas listas: o `group`
 * `SERVICES` só vem preenchido em parte dos produtos e os códigos variam por
 * catálogo. Por isso a classificação combina três camadas:
 *   1. o `group` do schema, quando vem como `SERVICES`;
 *   2. os códigos conhecidos de assistência/capitalização;
 *   3. a lista canônica de nomes de serviços e assistências (abaixo), com
 *      prefixos/palavras-chave para tolerar variações de sufixo do catálogo
 *      (ex.: "Assistência Cesta Básica (Cartão 6 x 250)").
 */

/**
 * Nomes canônicos que devem cair em "Serviços e Assistências".
 *
 * Os AUXÍLIOS não entram: auxílio paga capital e é cobertura
 * ("Auxílio Funeral Individual"); assistência é serviço prestado
 * ("Assistência Funeral Individual"). O catálogo traz os dois com nomes quase
 * iguais, e é só esse prefixo que os separa.
 */
const SERVICE_COVERAGE_NAMES = [
  'Assistência Funeral Individual',
  'Assistência Funeral Familiar',
  'Assistência Funeral Familiar Ampliado',
  'Assistência Funeral Simples Individual',
  'Assistência Funeral Simples Familiar',
  'Assistência Funeral Simples Familiar Ampliado',
  'Assistência Cesta Básica (Cartão 6 x 250)',
  'Assistência Cesta Natalidade',
  'Assistência Desconto em Medicamentos',
  'Assistência Fitness',
  'Assistência Nutricional',
  'Orientação Financeira',
  'Recolocação Profissional (Orientação)',
  'Assistência Proteção Digital',
  'Assistência Psicológica',
  'Assistência Rede Médica de Desconto',
  'Assistência Vítimas de Crime',
  'Assistência Empresarial',
  'Assistência Odontológica de Urgência - SOS Odonto',
  'Assistência Telemedicina - VitalMed',
  'Assistência Pet',
  'Checkup Lar Idosos',
  'Checkup Lar Kids',
  'Segunda Opinião Médica (Somi)',
];

/**
 * Prefixos que cobrem variações de nome dos itens da lista canônica.
 *
 * `AUXILIO` e `CESTA BASICA` saíram junto com os auxílios: o primeiro é o
 * prefixo deles, e o segundo existia só para pegar
 * "Cesta Básica/Auxílio Alimentação". "Assistência Cesta Básica (...)" segue
 * classificada pelo prefixo `ASSISTENCIA`.
 */
const SERVICE_NAME_PREFIXES = [
  'ASSISTENCIA',
  'CESTA NATALIDADE',
  'CHECKUP LAR',
  'ORIENTACAO FINANCEIRA',
  'RECOLOCACAO PROFISSIONAL',
  'SEGUNDA OPINIAO MEDICA',
];

/** Termos que identificam serviço/assistência em qualquer posição do nome. */
const SERVICE_NAME_KEYWORDS = [
  'ASSISTENCIA',
  'CAPITALIZACAO',
  'SORTEIO',
  'SOS ODONTO',
  'TELEMEDICINA',
  'VITALMED',
  'DESCONTO EM MEDICAMENTOS',
  'REDE MEDICA DE DESCONTO',
  'PROTECAO DIGITAL',
];

/** Uppercase sem acentos e com pontuação reduzida a espaço simples. */
function normalizeName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

const SERVICE_NAME_SET = new Set(SERVICE_COVERAGE_NAMES.map(normalizeName));

/** True quando o item pertence à lista "Serviços e Assistências". */
export function isServiceCoverage(
  code: string,
  name: string,
  group?: string
): boolean {
  const normalizedCode = (code ?? '').trim().toUpperCase();
  const normalizedName = normalizeName(name ?? '');

  if (group === 'SERVICES') return true;
  if (
    normalizedCode === 'ASSIST' ||
    normalizedCode.endsWith('ASS') ||
    normalizedCode === 'CAPITA' ||
    normalizedCode.endsWith('CAP')
  ) {
    return true;
  }
  if (!normalizedName) return false;
  if (SERVICE_NAME_SET.has(normalizedName)) return true;
  if (SERVICE_NAME_PREFIXES.some(prefix => normalizedName.startsWith(prefix))) {
    return true;
  }
  return SERVICE_NAME_KEYWORDS.some(keyword =>
    normalizedName.includes(keyword)
  );
}

/** Nomes canônicos expostos para testes/documentação. */
export const SERVICE_COVERAGE_CANONICAL_NAMES = SERVICE_COVERAGE_NAMES;
